import React, { useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import InstallationCompanyModal from './InstallationCompanyModal';
import { accessService } from '../../../services/central-api/access';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import Card from '../../../components/Card';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function InstallationCompany({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'InstallationCompanies'>>();
    const { user: authenticatedUser } = useAuth();

    const [installationCompanies, setInstallationCompanies] = useState<InstallationCompanyList[]>([]);
    const [selectedInstallationCompany, setSelectedInstallationCompany] = useState<InstallationCompany>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessInstallationCompanies', filters],
        queryFn: () => accessService.getInstallationCompanies(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setInstallationCompanies(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(installationCompany: InstallationCompany) {
        try {
            if (installationCompany?.id) {
                await accessService.deleteInstallationCompany(installationCompany.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();

            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('installationCompanies')}
                actions={<CustomButton
                    onPress={() => {
                        setSelectedInstallationCompany(undefined);
                        setModalVisible(true);
                    }}
                    text={translate('register')}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredInstallationCompanies')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[{
                            name: translate('name'),
                            selector: row => row.name,
                            grow: 2,
                            wrap: true
                        },
                        {
                            name: translate('cameraCount'),
                            selector: row => row.cameraCount,
                            grow: 2,
                        },
                        {
                            button: true,
                            cell: row => <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedInstallationCompany(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    onPress: () => {
                                        setSelectedInstallationCompany(row);
                                        setDeleteModalVisible(true);
                                    },
                                    variant: 'danger',
                                }]}

                            />
                        }]}
                        data={installationCompanies}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            {/* Delete Modal */}
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={String(selectedInstallationCompany?.name) || ''}
                onSubmit={async () => {
                    if (selectedInstallationCompany) {
                        await confirmDelete(selectedInstallationCompany);
                    }
                }}
            />

            {/* Create/Edit Modal */}
            <InstallationCompanyModal
                onClose={() => {
                    setSelectedInstallationCompany(undefined);
                    setModalVisible(false);
                    refetch();
                }}
                isModalVisible={isModalVisible}
                installationCompanyId={selectedInstallationCompany?.id}
            />
        </Container>
    );
}
